import { Calendar, Clock, MapPin, Heart } from 'lucide-react';
import { TagChip, MainTagChip } from './TagChip';
import { useState } from 'react';

interface EventCardProps {
  title: string;
  bannerUrl?: string;
  date: string;
  time: string;
  location?: string;
  mainTags?: Array<{ label: string; icon?: any }>;
  otherTags?: Array<{ label: string; color?: string }>;
  /** Lowest price across all participant types when at least one is paid. */
  priceFromRon?: number | null;
  onLike?: () => void;
  onCardClick?: () => void;
  isLiked?: boolean;
  disabled?: boolean;
}

export function EventCard({
  title,
  bannerUrl,
  date,
  time,
  location,
  mainTags = [],
  otherTags = [],
  priceFromRon,
  onLike,
  onCardClick,
  isLiked = false,
  disabled = false,
}: EventCardProps) {
  const [liked, setLiked] = useState(isLiked);

  const handleLikeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setLiked(!liked);
      onLike?.();
    }
  };

  const handleCardClick = () => {
    if (!disabled && onCardClick) {
      onCardClick();
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="overflow-hidden rounded-xl border bg-white shadow-sm transition-all hover:shadow-md active:scale-[0.98] disabled:opacity-50"
      style={{
        cursor: disabled ? 'not-allowed' : onCardClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {/* Banner */}
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-purple-100 to-purple-50">
        {bannerUrl ? (
          <img
            src={bannerUrl}
            alt={title}
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            className="h-full w-full"
            style={{
              background: 'linear-gradient(135deg, #f0e8ff 0%, #e8d5ff 100%)',
              backgroundImage:
                'radial-gradient(circle at 20% 50%, rgba(59, 11, 134, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 100, 23, 0.1) 0%, transparent 50%)',
            }}
          />
        )}

        {/* Main Tags Overlay */}
        {mainTags.length > 0 && (
          <div className="absolute left-3 top-3 flex flex-wrap gap-2">
            {mainTags.map((tag, index) => (
              <MainTagChip key={index} label={tag.label} icon={tag.icon} disabled={disabled} />
            ))}
          </div>
        )}

        {/* Like Button */}
        <button
          onClick={handleLikeClick}
          disabled={disabled}
          className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md transition-all hover:scale-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={liked ? 'Elimină de la favorite' : 'Adaugă la favorite'}
        >
          <Heart
            className="h-5 w-5 transition-all"
            fill={liked ? '#ff6417' : 'none'}
            stroke={liked ? '#ff6417' : '#1a1a1a'}
          />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Title */}
        <h3
          className="mb-3 line-clamp-2 text-lg font-semibold"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          {title}
        </h3>

        {/* Date, Time, Location */}
        <div
          className="mb-3 space-y-2 text-sm text-muted-foreground"
          style={{ fontFamily: 'var(--font-secondary)' }}
        >
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 flex-shrink-0" style={{ color: '#3b0b86' }} />
            <span>{date}</span>
            <Clock className="ml-2 h-4 w-4 flex-shrink-0" style={{ color: '#3b0b86' }} />
            <span>{time}</span>
          </div>

          {location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 flex-shrink-0" style={{ color: '#3b0b86' }} />
              <span className="line-clamp-1">{location}</span>
            </div>
          )}
        </div>

        {/* Other Tags */}
        {otherTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {otherTags.map((tag, index) => (
              <TagChip
                key={index}
                label={tag.label}
                color={tag.color}
                disabled={disabled}
              />
            ))}
          </div>
        )}

        {priceFromRon !== null && priceFromRon !== undefined && (
          <div className="mt-3 inline-block rounded-full bg-[var(--vivid-yellow-green)]/30 px-3 py-1 text-xs font-semibold text-[var(--vivid-purple)]">
            De la {priceFromRon} RON
          </div>
        )}
      </div>
    </div>
  );
}
